import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import useSeo from "@/lib/useSeo";
import { brandStyle, PoweredBy } from "@/lib/branding";
import { Lockup } from "@/components/Brand";

// Public participant page: a phone, a corridor, ninety seconds. No account, no shell, no contact details.
export default function PulsePage() {
  const { token } = useParams();
  useSeo({ title: "Quick pulse", description: "Five questions about a change at your workplace. Anonymous, no sign-in.", path: "/pulse" });
  const doneKey = `changeos_pulse_done_${token.slice(0, 12)}`;
  const [data, setData] = useState(null);
  const [state, setState] = useState(localStorage.getItem(doneKey) ? "done" : "loading");
  const [answers, setAnswers] = useState({});
  const [comment, setComment] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (state === "done") return;
    api.get(`/public/pulse/${encodeURIComponent(token)}`).then((r) => { setData(r.data); setState(r.data.valid ? "form" : "invalid"); }).catch(() => setState("invalid"));
  }, [token]); // eslint-disable-line
  const brand = data?.branding;
  const missing = data?.questions ? data.questions.filter((q) => !answers[q.key]) : [];
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (missing.length) { setErr(`Please answer ${missing.length === 1 ? "the last question" : `all five questions — ${missing.length} left`}.`); document.getElementById(`q-${missing[0].key}`)?.focus(); return; }
    setBusy(true);
    try {
      const { data: r } = await api.post(`/public/pulse/${encodeURIComponent(token)}`, { answers, comment });
      if (r.accepted) { localStorage.setItem(doneKey, "1"); setState("done"); } else setState("invalid");
    } catch (ex) { setErr(errMsg(ex)); setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-background text-foreground" style={brandStyle(brand)} data-testid="pulse-page" data-branded={brand ? "true" : "false"}>
      <a href="#pulse-main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-card focus:px-3 focus:py-2 focus:rounded-lg focus:text-sm focus:font-bold">Skip to questions</a>
      <header className="max-w-md mx-auto px-4 pt-6 pb-3 flex items-center justify-between gap-3">
        {brand ? (
          <div className="flex items-center gap-2 min-w-0 border-l-4 border-brand-raw pl-3" data-testid="pulse-practice-brand">
            {brand.logo_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${brand.logo_url}`} alt="" className="h-7 object-contain" />}
            <span className="text-sm font-bold truncate">{brand.display_name}</span>
          </div>
        ) : <Lockup height={20} bar="var(--brand-teal)" />}
        {brand && <PoweredBy />}
      </header>
      <main id="pulse-main" className="max-w-md mx-auto px-4 pb-16" aria-live="polite">
        {state === "loading" && <p className="text-sm text-muted-foreground py-10 text-center" data-testid="pulse-loading">Loading…</p>}
        {state === "invalid" && (
          <section className="pp-card p-6 mt-4" data-testid="pulse-invalid">
            <h1 className="font-head text-2xl font-extrabold">This link isn't available</h1>
            <p className="text-base text-subtle mt-3 leading-relaxed">It may have been used already, or the survey it belonged to has closed. If you were given a personal code, each one works once.</p>
            <p className="text-sm text-muted-foreground mt-4">Nothing about you was recorded by opening it.</p>
          </section>
        )}
        {state === "done" && (
          <section className="pp-card p-6 mt-4 text-center" data-testid="pulse-done">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center" aria-hidden="true"><Check size={24} /></div>
            <h1 className="font-head text-2xl font-extrabold mt-4">Thank you</h1>
            <p className="text-base text-subtle mt-2 leading-relaxed">Your answers have been added to your group's total. Nobody can see which answers were yours.</p>
          </section>
        )}
        {state === "form" && data && (
          <form onSubmit={submit} noValidate data-testid="pulse-form">
            <h1 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight mt-2">Five quick questions</h1>
            {data.context?.title && <p className="text-base text-subtle mt-2" data-testid="pulse-context">About <span className="font-bold text-foreground">{data.context.title}</span>{data.context.group && <> · for <span className="font-bold text-foreground">{data.context.group}</span></>}</p>}
            <p className="text-sm text-muted-foreground mt-2">Anonymous. About ninety seconds. Answer for yourself, not for your team.</p>
            <ol className="mt-6 space-y-6 list-none p-0">
              {data.questions.map((q, i) => (
                <li key={q.key}>
                  <fieldset className="pp-card p-4" data-testid={`pulse-q-${q.key}`} id={`q-${q.key}`} tabIndex={-1}>
                    <legend className="sr-only">Question {i + 1} of {data.questions.length}: {q.text}</legend>
                    <div aria-hidden="true"><div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{i + 1} of {data.questions.length}</div><p className="text-base font-bold mt-1 leading-snug">{q.text}</p></div>
                    <div className="mt-3 grid grid-cols-1 gap-2">
                      {data.scale.map((s) => (
                        <label key={s.value} className="relative flex items-center min-h-[48px] rounded-xl border border-border bg-card px-4 text-base font-semibold cursor-pointer transition-colors has-[:checked]:bg-primary has-[:checked]:text-primary-foreground has-[:checked]:border-primary hover:border-primary focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                          <input type="radio" name={q.key} value={s.value} checked={answers[q.key] === s.value} onChange={() => setAnswers({ ...answers, [q.key]: s.value })} data-testid={`pulse-${q.key}-${s.value}`} className="sr-only" />
                          <span>{s.label}</span>
                          {answers[q.key] === s.value && <Check size={18} className="ml-auto" aria-hidden="true" />}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                </li>
              ))}
            </ol>
            <div className="pp-card p-4 mt-6">
              <label htmlFor="pulse-comment" className="text-base font-bold leading-snug block">{data.comment.prompt} <span className="text-sm text-muted-foreground font-normal">(optional)</span></label>
              <textarea id="pulse-comment" data-testid="pulse-comment" className="pp-input mt-3 min-h-[96px] text-base" maxLength={data.comment.max_len} value={comment} onChange={(e) => setComment(e.target.value)} aria-describedby="pulse-comment-hint" />
              <div id="pulse-comment-hint" className="text-xs text-muted-foreground mt-1">{comment.length}/{data.comment.max_len}. Shown to the people leading the change as text only, separate from your other answers, and only once {data.floor} or more people in your group have answered.</div>
            </div>
            {err && <div role="alert" className="mt-4 rounded-xl bg-danger-50 border border-danger-200 text-danger-800 text-sm px-4 py-3" data-testid="pulse-error">{err}</div>}
            <button type="submit" disabled={busy} data-testid="pulse-submit" className="pp-btn-dark w-full mt-6 min-h-[52px] text-base">{busy ? "Sending…" : "Send my answers"}</button>
            <details className="mt-8 rounded-2xl border border-border-soft bg-muted/40 p-4 text-sm" data-testid="pulse-how">
              <summary className="font-bold cursor-pointer min-h-[44px] flex items-center">{data.how_it_works.heading}</summary>
              <div className="mt-3 space-y-3 text-subtle leading-relaxed">
                <div><div className="font-bold text-foreground">What is stored</div><ul className="list-disc pl-5 mt-1 space-y-1">{data.how_it_works.stored.map((s) => <li key={s}>{s}</li>)}</ul></div>
                <div><div className="font-bold text-foreground">What is not stored</div><ul className="list-disc pl-5 mt-1 space-y-1">{data.how_it_works.not_stored.map((s) => <li key={s}>{s}</li>)}</ul></div>
                <p>{data.how_it_works.reporting}</p>
                <p>{data.how_it_works.honesty}</p>
              </div>
            </details>
          </form>
        )}
      </main>
    </div>
  );
}
